// Overview / Home page API client.
//
// Single backend round-trip to GET /dashboard/overview. Returns the 6 KPI
// stats, 5 setup states, ~10 recent donations, and 1-2 AI suggestions in one
// shot — landing-surface TTI is critical for v2.
//
// The backend endpoint is authoritative. DEV mode retains a deterministic
// 404 fallback for isolated local rendering; production never uses the mock.
//
// Per .forge/plans/v2-overview.md.

import axios from "axios";
import { apiClient } from "./client";

export type SetupStepId =
  | "payments"
  | "brand"
  | "locale"
  | "first-designation"
  | "first-campaign";

export interface SetupStep {
  id: SetupStepId;
  done: boolean;
}

export interface OverviewStats {
  primary_currency: string;
  today_minor_units: number;
  today_count: number;
  today_new_donor_count: number;
  week_minor_units: number;
  week_count: number;
  week_new_donor_count: number;
  month_minor_units: number;
  month_count: number;
  month_new_donor_count: number;
  ytd_minor_units: number;
  ytd_count: number;
  ytd_new_donor_count: number;
  recurring_mrr_minor_units: number;
  active_campaigns_count: number;
}

export interface OverviewRecentDonation {
  id: string;
  donor_name: string;
  designation_name: string;
  amount_minor_units: number;
  created_at: string;
}

export type AISuggestionKind =
  | "campaign_opportunity"
  | "at_risk_donor"
  | "seasonal_template"
  | string;

export interface AISuggestion {
  id: string;
  kind: AISuggestionKind;
  title: string;
  body: string;
  cta?: { label: string; route?: string };
}

export interface OverviewResponse {
  setup_complete: boolean;
  setup_steps: SetupStep[];
  stats: OverviewStats;
  recent_donations: OverviewRecentDonation[];
  ai_suggestions: AISuggestion[];
}

// Deterministic mock so vitest + DEV-mode rendering is stable. NOT exported as
// a default — must be reached via fetchOverview() through the 404 path so we
// can't accidentally bypass real API calls in production.
const MOCK_OVERVIEW: OverviewResponse = {
  setup_complete: false,
  setup_steps: [
    { id: "payments", done: true },
    { id: "brand", done: true },
    { id: "locale", done: false },
    { id: "first-designation", done: false },
    { id: "first-campaign", done: false },
  ],
  stats: {
    primary_currency: "USD",
    today_minor_units: 42_500,
    today_count: 4,
    today_new_donor_count: 2,
    week_minor_units: 318_200,
    week_count: 23,
    week_new_donor_count: 8,
    month_minor_units: 1_492_750,
    month_count: 104,
    month_new_donor_count: 31,
    ytd_minor_units: 18_421_000,
    ytd_count: 927,
    ytd_new_donor_count: 184,
    recurring_mrr_minor_units: 184_500,
    active_campaigns_count: 3,
  },
  recent_donations: [
    {
      id: "dn-1",
      donor_name: "Alice Park",
      designation_name: "Building Fund",
      amount_minor_units: 25_000,
      created_at: "2026-05-16T07:32:00Z",
    },
    {
      id: "dn-2",
      donor_name: "Brian Okafor",
      designation_name: "Spring Appeal",
      amount_minor_units: 5_000,
      created_at: "2026-05-16T07:18:00Z",
    },
    {
      id: "dn-3",
      donor_name: "Carmen Diaz",
      designation_name: "Year-end Match",
      amount_minor_units: 10_000,
      created_at: "2026-05-16T06:55:00Z",
    },
    {
      id: "dn-4",
      donor_name: "Devon Liu",
      designation_name: "Building Fund",
      amount_minor_units: 50_000,
      created_at: "2026-05-16T06:12:00Z",
    },
    {
      id: "dn-5",
      donor_name: "Esther Mwangi",
      designation_name: "General Fund",
      amount_minor_units: 2_500,
      created_at: "2026-05-16T05:48:00Z",
    },
    {
      id: "dn-6",
      donor_name: "Farouk Hassan",
      designation_name: "Spring Appeal",
      amount_minor_units: 7_500,
      created_at: "2026-05-16T05:21:00Z",
    },
    {
      id: "dn-7",
      donor_name: "Grace Tanaka",
      designation_name: "General Fund",
      amount_minor_units: 15_000,
      created_at: "2026-05-16T04:55:00Z",
    },
    {
      id: "dn-8",
      donor_name: "Henry Adebayo",
      designation_name: "Building Fund",
      amount_minor_units: 3_000,
      created_at: "2026-05-16T04:30:00Z",
    },
  ],
  ai_suggestions: [
    {
      id: "sg-1",
      kind: "campaign_opportunity",
      title: "Time to launch a summer matching campaign",
      body: "Based on giving patterns last June, a 30-day matching campaign typically lifts donations 18%. Three of your top donors gave matching gifts last summer.",
      cta: { label: "Draft campaign", route: "/campaigns/new" },
    },
    {
      id: "sg-2",
      kind: "at_risk_donor",
      title: "5 monthly donors are showing churn signals",
      body: "Their average gift size dropped 30% in the last 60 days. Reaching out personally now usually retains 4 of 5.",
      cta: { label: "View at-risk donors", route: "/donors?filter=at_risk" },
    },
  ],
};

export async function fetchOverview(): Promise<OverviewResponse> {
  try {
    const r = await apiClient().get<OverviewResponse>("/dashboard/overview");
    return r.data;
  } catch (err) {
    // 404 = endpoint not yet deployed → DEV mock so the page still renders.
    // Production 404 is a real error (CDN / proxy misconfig); only DEV falls
    // back to the mock. Non-404 errors always re-throw.
    if (
      import.meta.env.DEV &&
      axios.isAxiosError(err) &&
      err.response?.status === 404
    ) {
      return MOCK_OVERVIEW;
    }
    throw err;
  }
}

// Exposed for tests so they can assert against the same fixtures the page
// renders in DEV. NOT consumed by production code paths.
export const __TEST_MOCK_OVERVIEW = MOCK_OVERVIEW;
